import { afterInteraction } from '../common/afterInteraction';

export type MixpanelClient = {
  track: (event: string, payload?: Record<string, unknown>) => void;
  identify: (id: string) => void;
  people: { set: (attrs: Record<string, unknown>) => void };
};

type MixpanelQueueItem
  = | { type: 'track'; args: [string, Record<string, unknown>?]; timestamp: number }
    | { type: 'identify'; args: [string] }
    | { type: 'people.set'; args: [Record<string, unknown>] };

export default defineNuxtPlugin((nuxtApp) => {
  const app = useNuxtApp();
  const config = useRuntimeConfig();
  const token = config.public.MIXPANEL_TOKEN || 'd8038d374838b13ef8ac565b08be6135';

  const queue: MixpanelQueueItem[] = [];
  let realMixpanel: typeof import('mixpanel-browser') | null = null;

  const peopleStub = {
    set: (attrs: Record<string, unknown>) => {
      if (realMixpanel) {
        realMixpanel.people.set(attrs);
      } else {
        queue.push({ type: 'people.set', args: [attrs] });
      }
    },
  };

  const mixpanelStub = {
    track: (eventName: string, payload?: Record<string, unknown>) => {
      if (realMixpanel) {
        realMixpanel.track(eventName, payload);
      } else {
        queue.push({ type: 'track', args: [eventName, payload], timestamp: Date.now() });
      }
    },
    identify: (id: string) => {
      if (realMixpanel) {
        realMixpanel.identify(id);
      } else {
        queue.push({ type: 'identify', args: [id] });
      }
    },
    people: peopleStub,
  };

  function replayQueue() {
    if (!realMixpanel) return;
    for (const item of queue) {
      try {
        if (item.type === 'track') {
          realMixpanel.track(item.args[0], { time: Math.floor(item.timestamp / 1000), ...item.args[1] });
        } else if (item.type === 'identify') {
          realMixpanel.identify(item.args[0]);
        } else if (item.type === 'people.set') {
          realMixpanel.people.set(item.args[0]);
        }
      } catch (error: unknown) {
        app.$logger.note({
          category: 'mixpanel',
          message: 'queued call failed',
          level: 'warning',
          cause: error,
        });
      }
    }
    queue.length = 0;
  }

  nuxtApp.hook('app:mounted', () => {
    afterInteraction().then(() => import('mixpanel-browser')).then((mod) => {
      realMixpanel = mod.default;
      realMixpanel.init(token, { debug: true, ignore_dnt: true, api_host: 'https://api-eu.mixpanel.com' });
      replayQueue();
    }).catch((error: unknown) => {
      app.$logger.note({
        category: 'mixpanel',
        message: 'sdk failed to load',
        level: 'warning',
        cause: error,
      });
    });
  });

  return {
    provide: {
      mixpanel: mixpanelStub,
    },
  };
});
